import { Source } from '@/app/constants/import/source'
import { FileType } from '@/app/constants/fileType'
import { ProcessedStatus } from '@/app/models/import/processedStatus'
import { HistoricalFileGroupDto } from '@/lib/api/company/fileProcessing/historical'
import { getSource } from '@/app/services/import/sourceResolver'
import { getReadStatus } from '@/app/services/import/readStatusResolver'
import { getCertificateStatus } from '@/app/services/import/certificateStatusResolver'
import { getProcessedStatus } from '@/app/services/import/processedStatusResolver'
import { getFileType } from '@/app/services/import/fileTypeResolver'
import { FileTypeFilter, HistoryRow, StatusFilter } from './types'

const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function fileExtension(fileName: string): string {
  const match = /\.[A-Za-z0-9]{1,8}$/.exec(fileName ?? '')
  return match ? match[0] : ''
}

export function buildDisplayFileName(file: {
  fileName: string
  driver: string | null
  downloadTime: string | null
}): string {
  const fallback = file.fileName ?? ''
  const driver = (file.driver ?? '').trim()
  if (!driver || !file.downloadTime) return fallback

  const readAt = new Date(file.downloadTime)
  if (isNaN(readAt.getTime())) return fallback

  const stamp = `${readAt.getFullYear()}-${pad2(readAt.getMonth() + 1)}-${pad2(readAt.getDate())}_${pad2(readAt.getHours())}-${pad2(readAt.getMinutes())}`
  const base = `${driver}_${stamp}`
    .replace(ILLEGAL_FILENAME_CHARS, '')
    .replace(/\s+/g, '_')

  return `${base}${fileExtension(fallback)}`
}

export function flattenGroups(groups: HistoricalFileGroupDto[]): HistoryRow[] {
  const rows: HistoryRow[] = []
  for (const g of groups) {
    for (const f of g.files ?? []) {
      rows.push({
        id: String(f.id),
        groupId: g.id != null ? String(g.id) : '',
        importDate: g.date,
        fileName: f.fileName,
        displayName: buildDisplayFileName({
          fileName: f.fileName,
          driver: f.driver,
          downloadTime: f.downloadTime,
        }),
        fileType: getFileType(f.fileType as unknown as number),
        source: getSource(f.source as unknown as number),
        driver: f.driver,
        vehicle: f.vehicle,
        readStatus: getReadStatus(f.readStatus as unknown as number),
        certificateStatus: getCertificateStatus(f.certificateStatus as unknown as number),
        processedStatus: getProcessedStatus(f.processedStatus as unknown as number),
        cardReportDetailsJson: f.cardReportDetailsJson ?? null,
        format: f.format ?? null,
        validLines: f.validLines ?? 0,
        invalidLines: f.invalidLines ?? 0,
        logs: f.logs ?? null,
        raw: {
          cardValidFrom: f.cardValidFrom,
          cardExpirationDate: f.cardExpirationDate,
          firstActivityTime: f.firstActivityTime,
          lastActivityTime: f.lastActivityTime,
          downloadTime: f.downloadTime,
        },
      })
    }
  }
  return rows
}

export function matchesFileType(row: HistoryRow, filter: FileTypeFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'driverCard') return row.source === Source.Card
  if (filter === 'vehicle') return row.fileType === FileType.Ddd && row.source === Source.File
  if (filter === 'gps') return row.fileType === FileType.Gps
  if (filter === 'tms') return row.fileType === FileType.Tms
  if (filter === 'chart') return row.fileType === FileType.Chart
  if (filter === 'other')
    return (
      row.fileType !== FileType.Ddd &&
      row.fileType !== FileType.Gps &&
      row.fileType !== FileType.Tms &&
      row.fileType !== FileType.Chart
    )
  return true
}

export function matchesStatus(row: HistoryRow, filter: StatusFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'compatible') return row.processedStatus === ProcessedStatus.All
  if (filter === 'partial') return row.processedStatus === ProcessedStatus.Some
  if (filter === 'incompatible') return row.processedStatus === ProcessedStatus.None
  return true
}

export function matchesText(row: HistoryRow, q: string): boolean {
  if (!q) return true
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  const haystack = [row.driver ?? '', row.vehicle ?? '', row.fileName ?? '', row.displayName ?? '']
    .join(' ')
    .toLowerCase()
  return haystack.includes(needle)
}

export function matchesDate(row: HistoryRow, isoDay: string): boolean {
  if (!isoDay) return true
  return (row.importDate || '').slice(0, 10) === isoDay
}

export function formatDateTime(iso: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
